/**
 * UserMenuConfigEditor.jsx - Editor per voci del menu utente in header
 * Gestisce blueprint_user_menu_items via CMS
 */

import React, { useState, useEffect } from 'react';
import supabase from '../../lib/supabase';
import { 
  Plus, Save, Trash2, Edit, Check, X, Loader2, 
  ChevronUp, ChevronDown, Eye, EyeOff,
  LayoutDashboard, Settings, HelpCircle, LogOut, 
  User, ExternalLink, Building2
} from 'lucide-react';

// Available icons for user menu items
const ICON_OPTIONS = [
  { value: 'LayoutDashboard', label: 'Dashboard', Icon: LayoutDashboard },
  { value: 'Settings', label: 'Impostazioni', Icon: Settings },
  { value: 'HelpCircle', label: 'Supporto', Icon: HelpCircle },
  { value: 'User', label: 'Utente', Icon: User },
  { value: 'Building2', label: 'Azienda', Icon: Building2 },
  { value: 'ExternalLink', label: 'Link esterno', Icon: ExternalLink },
  { value: 'LogOut', label: 'Logout', Icon: LogOut }
];

const getIconComponent = (iconName) => {
  const found = ICON_OPTIONS.find(i => i.value === iconName);
  return found?.Icon || User;
};

const UserMenuConfigEditor = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItem, setNewItem] = useState({
    slug: '',
    label_it: '',
    label_en: '',
    path: '',
    icon: 'User',
    action: null,
    target: '_self',
    is_active: true,
    requires_admin: false,
    is_separator: false,
    order_index: 0
  });

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('blueprint_user_menu_items')
        .select('*')
        .order('order_index', { ascending: true });
      
      if (error) throw error;
      setItems(data || []);
    } catch (err) {
      console.error('Error loading user menu items:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveItem = async (item) => {
    setSaving(true);
    try {
      if (item.id) {
        const { error } = await supabase
          .from('blueprint_user_menu_items')
          .update({
            slug: item.slug,
            label_it: item.label_it,
            label_en: item.label_en,
            path: item.path,
            icon: item.icon,
            action: item.action,
            target: item.target,
            is_active: item.is_active,
            requires_admin: item.requires_admin,
            is_separator: item.is_separator,
            order_index: item.order_index
          })
          .eq('id', item.id);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('blueprint_user_menu_items')
          .insert([{
            slug: item.slug,
            label_it: item.label_it,
            label_en: item.label_en,
            path: item.path,
            icon: item.icon,
            action: item.action,
            target: item.target,
            is_active: item.is_active,
            requires_admin: item.requires_admin,
            is_separator: item.is_separator,
            order_index: items.length
          }]);
        
        if (error) throw error;
      }
      
      await loadItems();
      setEditingItem(null);
      setShowAddForm(false);
      setNewItem({
        slug: '',
        label_it: '',
        label_en: '',
        path: '',
        icon: 'User',
        action: null,
        target: '_self',
        is_active: true,
        requires_admin: false,
        is_separator: false,
        order_index: 0
      });
    } catch (err) {
      console.error('Error saving item:', err);
      alert('Errore: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Eliminare questa voce?')) return;
    
    try {
      const { error } = await supabase
        .from('blueprint_user_menu_items')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      await loadItems();
    } catch (err) {
      console.error('Error deleting item:', err);
      alert('Errore: ' + err.message);
    }
  };

  const moveItem = async (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= items.length) return;
    
    const newItems = [...items];
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
    
    try {
      for (let i = 0; i < newItems.length; i++) {
        await supabase
          .from('blueprint_user_menu_items')
          .update({ order_index: i })
          .eq('id', newItems[i].id);
      }
      setItems(newItems);
    } catch (err) {
      console.error('Error reordering:', err);
    }
  };

  const toggleActive = async (item) => {
    try {
      const { error } = await supabase
        .from('blueprint_user_menu_items')
        .update({ is_active: !item.is_active })
        .eq('id', item.id);
      
      if (error) throw error;
      await loadItems();
    } catch (err) {
      console.error('Error toggling active:', err);
    }
  };

  const ItemForm = ({ item, onSave, onCancel }) => {
    const [formData, setFormData] = useState(item);
    
    return (
      <div className="bg-[#1a1a18] rounded-lg p-4 border border-white/10 space-y-4">
        {/* Separator option */}
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.is_separator}
            onChange={(e) => setFormData({ ...formData, is_separator: e.target.checked })}
            className="w-4 h-4 rounded"
          />
          <span className="text-sm text-white/70">Questo è un separatore (linea divisoria)</span>
        </label>

        {!formData.is_separator && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-white/50 mb-1">Slug (ID univoco)</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  className="w-full bg-[#0a0a0a] border border-white/10 rounded px-3 py-2 text-sm text-white"
                  placeholder="es. settings"
                />
              </div>
              <div>
                <label className="block text-xs text-white/50 mb-1">Path (URL)</label>
                <input
                  type="text"
                  value={formData.path}
                  onChange={(e) => setFormData({ ...formData, path: e.target.value })}
                  className="w-full bg-[#0a0a0a] border border-white/10 rounded px-3 py-2 text-sm text-white"
                  placeholder="es. /settings"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-white/50 mb-1">Label IT</label>
                <input
                  type="text"
                  value={formData.label_it}
                  onChange={(e) => setFormData({ ...formData, label_it: e.target.value })}
                  className="w-full bg-[#0a0a0a] border border-white/10 rounded px-3 py-2 text-sm text-white"
                  placeholder="Impostazioni"
                />
              </div>
              <div>
                <label className="block text-xs text-white/50 mb-1">Label EN</label>
                <input
                  type="text"
                  value={formData.label_en || ''}
                  onChange={(e) => setFormData({ ...formData, label_en: e.target.value })}
                  className="w-full bg-[#0a0a0a] border border-white/10 rounded px-3 py-2 text-sm text-white"
                  placeholder="Settings"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-white/50 mb-1">Icona</label>
                <select
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  className="w-full bg-[#0a0a0a] border border-white/10 rounded px-3 py-2 text-sm text-white"
                >
                  {ICON_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-white/50 mb-1">Azione speciale</label>
                <select
                  value={formData.action || ''}
                  onChange={(e) => setFormData({ ...formData, action: e.target.value || null })}
                  className="w-full bg-[#0a0a0a] border border-white/10 rounded px-3 py-2 text-sm text-white"
                >
                  <option value="">Nessuna (navigazione normale)</option>
                  <option value="logout">Logout</option>
                  <option value="external_link">Link esterno</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-white/50 mb-1">Target</label>
                <select
                  value={formData.target}
                  onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                  className="w-full bg-[#0a0a0a] border border-white/10 rounded px-3 py-2 text-sm text-white"
                >
                  <option value="_self">Stessa finestra</option>
                  <option value="_blank">Nuova finestra</option>
                </select>
              </div>
            </div>
            
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm text-white/70">Attivo</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.requires_admin}
                  onChange={(e) => setFormData({ ...formData, requires_admin: e.target.checked })}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm text-white/70">Solo Admin</span>
              </label>
            </div>
          </>
        )}
        
        <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-white/60 hover:text-white"
          >
            Annulla
          </button>
          <button
            onClick={() => onSave(formData)}
            disabled={saving || (!formData.is_separator && (!formData.slug || !formData.label_it))}
            className="px-4 py-2 text-sm bg-[#B08B5C] text-black rounded font-medium hover:bg-[#C9A46E] disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salva
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#B08B5C]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-medium text-white">User Menu (Header)</h2>
          <p className="text-white/50 text-sm mt-1">Gestisci le voci del dropdown utente nell'header</p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#B08B5C] text-black rounded-lg text-sm font-medium hover:bg-[#C9A46E]"
        >
          <Plus className="w-4 h-4" />
          Aggiungi voce
        </button>
      </div>

      {showAddForm && (
        <ItemForm
          item={newItem}
          onSave={saveItem}
          onCancel={() => setShowAddForm(false)}
        />
      )}

      <div className="space-y-2">
        {items.map((item, index) => {
          const IconComponent = getIconComponent(item.icon);
          
          if (editingItem?.id === item.id) {
            return (
              <ItemForm
                key={item.id}
                item={editingItem}
                onSave={saveItem}
                onCancel={() => setEditingItem(null)}
              />
            );
          }
          
          // Separator rendering
          if (item.is_separator) {
            return (
              <div
                key={item.id}
                className={`flex items-center gap-4 p-3 rounded-lg border ${
                  item.is_active ? 'bg-[#1a1a18] border-white/10' : 'bg-[#0a0a0a] border-white/5 opacity-50'
                }`}
              >
                <div className="flex flex-col gap-1">
                  <button onClick={() => moveItem(index, -1)} disabled={index === 0} className="p-1 text-white/30 hover:text-white disabled:opacity-20">
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button onClick={() => moveItem(index, 1)} disabled={index === items.length - 1} className="p-1 text-white/30 hover:text-white disabled:opacity-20">
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex-1 border-t border-white/20" />
                <span className="text-xs text-white/30">SEPARATORE</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => toggleActive(item)} className={`p-2 rounded-lg ${item.is_active ? 'text-green-400' : 'text-white/30'}`}>
                    {item.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button onClick={() => deleteItem(item.id)} className="p-2 text-red-400/50 hover:text-red-400 rounded-lg">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          }
          
          return (
            <div
              key={item.id}
              className={`flex items-center gap-4 p-4 rounded-lg border transition-colors ${
                item.is_active ? 'bg-[#1a1a18] border-white/10' : 'bg-[#0a0a0a] border-white/5 opacity-50'
              }`}
            >
              <div className="flex flex-col gap-1">
                <button onClick={() => moveItem(index, -1)} disabled={index === 0} className="p-1 text-white/30 hover:text-white disabled:opacity-20">
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button onClick={() => moveItem(index, 1)} disabled={index === items.length - 1} className="p-1 text-white/30 hover:text-white disabled:opacity-20">
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
              
              <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center">
                <IconComponent className={`w-5 h-5 ${item.action === 'logout' ? 'text-red-400' : 'text-[#B08B5C]'}`} />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-white">{item.label_it}</span>
                  {item.target === '_blank' && (
                    <ExternalLink className="w-3 h-3 text-white/30" />
                  )}
                  {item.action === 'logout' && (
                    <span className="px-1.5 py-0.5 text-[10px] bg-red-500/20 text-red-400 rounded">LOGOUT</span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-white/40">
                  <span>{item.path || 'N/A'}</span>
                  <span>•</span>
                  <span>{item.slug}</span>
                  {item.requires_admin && (
                    <>
                      <span>•</span>
                      <span className="text-amber-400">Admin only</span>
                    </>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <button onClick={() => toggleActive(item)} className={`p-2 rounded-lg transition-colors ${item.is_active ? 'text-green-400 hover:bg-green-500/10' : 'text-white/30 hover:bg-white/5'}`}>
                  {item.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
                <button onClick={() => setEditingItem(item)} className="p-2 text-white/50 hover:text-white hover:bg-white/5 rounded-lg">
                  <Edit className="w-4 h-4" />
                </button>
                <button onClick={() => deleteItem(item.id)} className="p-2 text-red-400/50 hover:text-red-400 hover:bg-red-500/10 rounded-lg">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {items.length === 0 && !showAddForm && (
        <div className="text-center py-12 text-white/40">
          <p>Nessuna voce di menu configurata</p>
          <button onClick={() => setShowAddForm(true)} className="mt-2 text-[#B08B5C] hover:underline">
            Aggiungi la prima voce
          </button>
        </div>
      )}
    </div>
  );
};

export default UserMenuConfigEditor;
